import { useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import DeleteIcon from '@mui/icons-material/Delete';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { post } from 'api';
import { enqueueSnackbar } from 'notistack';
import * as yup from 'yup';

const stockSchema = yup.object({
  vendor: yup.string().required('Vendor selection is mandatory'),
  remark: yup.string().nullable(),
  poFile: yup
    .mixed()
    .required('PO file is required')
    .test('fileType', 'Only PDF / JPG / PNG allowed', (file) => {
      if (!file) return false;
      return ['application/pdf', 'image/png', 'image/jpeg'].includes(file.type);
    }),
  amount: yup.number().required('Amount is required'),
});

export default function StockUpdateDialog({ open, onClose, vendors, products }) {
  const {
    control,
    handleSubmit,
    // setValue,
    watch,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(stockSchema),
    defaultValues: { vendor: '', remark: '', poFile: null },
  });

  const [selectedProduct, setSelectedProduct] = useState(null);
  const [qnt, setQnt] = useState('');
  const [items, setItems] = useState([]);

  const handleAdd = () => {
    if (!selectedProduct || !qnt) return;

    setItems((prev) => [
      ...prev,
      {
        productId: selectedProduct._id,
        productName: selectedProduct.productName,
        unit: selectedProduct.unitType,
        qnt: Number(qnt),
      },
    ]);

    setSelectedProduct(null);
    setQnt('');
  };

  const deleteItem = (id) => setItems((p) => p.filter((i) => i.id !== id));

  const submitStock = async (data) => {
    try {
      const formData = new FormData();
      formData.append('poImage', data.poFile);
      formData.append('remark', data.remark);
      formData.append('vendor', data.vendor);
      formData.append('amount', data.amount);
      formData.append('products', JSON.stringify(items));

      const res = await post('users/create_addInventory', formData);

      if (res.status == 200) {
        enqueueSnackbar(res.message || 'Stock Update Successfully', {
          variant: 'success',
          autoHideDuration: 3000,
        });
      } else {
        enqueueSnackbar(res.message || 'Error While Updateing Stock Try Again Latter', {
          variant: 'error',
          autoHideDuration: 3000,
        });
      }
    } catch (error) {
      enqueueSnackbar(error.message || 'Error While Updateing Stock Try Again Latter', {
        variant: 'error',
        autoHideDuration: 3000,
      });
    } finally {
      onClose();
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="md"
      component="form"
      onSubmit={handleSubmit(submitStock)}
    >
      {/* HEADER */}
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ p: 1 }}>
        <DialogTitle sx={{ fontSize: 20, fontWeight: 700 }}>Stock Update Entry</DialogTitle>
        <IconButton onClick={onClose}>
          <CloseIcon />
        </IconButton>
      </Stack>

      {/* BODY */}
      <DialogContent sx={{ px: 2 }}>
        {/* VENDOR */}
        <Grid container spacing={2}>
          <Grid item size={{ xs: 12, sm: 6 }}>
            <Controller
              control={control}
              name="vendor"
              render={({ field }) => (
                <TextField
                  {...field}
                  fullWidth
                  select
                  label="Select Vendor"
                  error={!!errors.vendor}
                  helperText={errors.vendor?.message}
                >
                  {vendors.map((v) => (
                    <MenuItem key={v._id} value={v._id}>
                      {v.vendorName}
                    </MenuItem>
                  ))}
                </TextField>
              )}
            />
          </Grid>
          <Grid item size={{ xs: 12, sm: 6 }}>
            <Controller
              control={control}
              name="amount"
              render={({ field }) => (
                <TextField
                  {...field}
                  type="number"
                  fullWidth
                  label="Enter Amount"
                  error={!!errors.amount}
                  helperText={errors.amount?.message}
                />
              )}
            />
          </Grid>
        </Grid>

        {/* PRODUCT ADD BOX */}
        <Paper variant="outlined" sx={{ p: 2, mt: 3, borderRadius: 2 }}>
          <Typography fontWeight={600} mb={2}>
            Add Stock Item
          </Typography>

          <Stack direction="row" spacing={2} flexWrap="wrap">
            {/* Product Select */}
            <TextField
              select
              label="Product"
              sx={{ minWidth: 250 }}
              value={selectedProduct?._id || ''}
              onChange={(e) => setSelectedProduct(products.find((p) => p._id === e.target.value))}
            >
              <MenuItem value="">Select...</MenuItem>
              {products.map((p) => (
                <MenuItem key={p._id} value={p._id}>
                  {p.productName}
                </MenuItem>
              ))}
            </TextField>

            {/* Quantity */}
            <TextField
              disabled={!selectedProduct}
              type="number"
              label="Quantity"
              value={qnt}
              sx={{ width: 110 }}
              onChange={(e) => setQnt(e.target.value)}
            />

            {/* UNIT INFO */}
            {selectedProduct && (
              <Typography mt={1.3}>
                Unit: <b>{selectedProduct.unitType}</b>
              </Typography>
            )}

            {/* ADD BTN */}
            <IconButton color="primary" onClick={handleAdd}>
              <AddIcon />
            </IconButton>
          </Stack>
        </Paper>

        {/* TABLE */}
        {items.length > 0 && (
          <Paper variant="outlined" sx={{ mt: 3, borderRadius: 2 }}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>#</TableCell>
                  <TableCell>Product</TableCell>
                  <TableCell>Quantity</TableCell>
                  <TableCell>Unit</TableCell>
                  <TableCell />
                </TableRow>
              </TableHead>

              <TableBody>
                {items.map((i, idx) => (
                  <TableRow key={i.id}>
                    <TableCell>{idx + 1}</TableCell>
                    <TableCell>{i.productName}</TableCell>
                    <TableCell>{i.qnt}</TableCell>
                    <TableCell>{i.unit}</TableCell>
                    <TableCell>
                      <IconButton color="error" onClick={() => deleteItem(i.id)}>
                        <DeleteIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Paper>
        )}

        {/* PO FILE UPLOAD */}
        <Grid item xs={12} mt={3}>
          <Controller
            name="poFile"
            control={control}
            render={({ field }) => (
              <Stack>
                <Button component="label" variant="outlined">
                  Upload PO (PDF/Image)
                  <input
                    type="file"
                    hidden
                    accept="application/pdf,image/*"
                    onChange={(e) => field.onChange(e.target.files[0])}
                  />
                </Button>
                <Typography mt={1} fontSize={13} color="error">
                  {errors.poFile?.message}
                </Typography>
                {watch('poFile') && (
                  <Typography fontSize={14} mt={1}>
                    File: {watch('poFile').name}
                  </Typography>
                )}
              </Stack>
            )}
          />
        </Grid>

        {/* REMARK */}
        <Grid item xs={12} mt={3}>
          <Controller
            control={control}
            name="remark"
            render={({ field }) => (
              <TextField {...field} fullWidth multiline rows={3} label="Remark (optional)" />
            )}
          />
        </Grid>
      </DialogContent>

      {/* FOOTER */}
      <DialogActions sx={{ p: 2 }}>
        <Button variant="outlined" onClick={onClose}>
          Cancel
        </Button>
        <Button variant="contained" type="submit" disabled={items.length === 0}>
          Save Stock Entry
        </Button>
      </DialogActions>
    </Dialog>
  );
}
